import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { 
  Heart, 
  Brain, 
  Activity, 
  Shield, 
  Clock, 
  Users, 
  Video, 
  MessageCircle, 
  Calendar, 
  Phone,
  Mail,
  MapPin,
  Stethoscope,
  AlertCircle,
  CheckCircle,
  Star,
  ArrowRight,
  Plus,
  Play,
  Pause,
  Settings,
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  Eye,
  Lock,
  Unlock,
  Zap,
  Wifi,
  WifiOff,
  Bluetooth,
  Battery,
  Signal,
  Headphones,
  Mic,
  Camera,
  Monitor,
  Smartphone,
  Tablet,
  HelpCircle,
  Info,
  X,
  Check,
  ChevronRight,
  ChevronDown,
  Menu,
  Grid,
  List,
  Bookmark,
  Share,
  Flag,
  Award,
  Target,
  TrendingUp,
  BarChart3,
  PieChart,
  LineChart
} from 'lucide-react';

export const DesignShowcase = () => {
  const [activeTab, setActiveTab] = useState('buttons');
  const [progress, setProgress] = useState(75);
  const [loading, setLoading] = useState(false);

  const medicalIcons = [
    { icon: Heart, name: 'Heart', theme: 'medical', description: 'Cardiology & Heart Health' },
    { icon: Brain, name: 'Brain', theme: 'therapy', description: 'Mental Health & Neurology' },
    { icon: Activity, name: 'Activity', theme: 'success', description: 'Vital Signs Monitoring' },
    { icon: Stethoscope, name: 'Stethoscope', theme: 'medical', description: 'General Medical Care' },
    { icon: Shield, name: 'Shield', theme: 'info', description: 'HIPAA & Data Protection' },
    { icon: AlertCircle, name: 'Alert', theme: 'emergency', description: 'Emergency Situations' },
    { icon: CheckCircle, name: 'Check', theme: 'success', description: 'Treatment Completed' },
    { icon: Clock, name: 'Clock', theme: 'warning', description: 'Appointment Scheduling' }
  ];

  const buttonVariants = [
    { variant: 'default', size: 'md', label: 'Default Button', className: '' },
    { variant: 'primary', size: 'lg', label: 'Primary Large', className: 'bg-clinic-blue-600 hover:bg-clinic-blue-700' },
    { variant: 'secondary', size: 'xl', label: 'Secondary XL', className: 'btn-xl bg-clinic-gray-200 hover:bg-clinic-gray-300 text-clinic-gray-800' },
    { variant: 'emergency', size: 'lg', label: 'Emergency', className: 'btn-emergency' },
    { variant: 'success', size: 'md', label: 'Success', className: 'btn-success' },
    { variant: 'warning', size: 'md', label: 'Warning', className: 'btn-warning' },
    { variant: 'ghost', size: 'md', label: 'Ghost', className: 'btn-ghost' },
    { variant: 'pill', size: 'md', label: 'Pill Shape', className: 'btn-pill bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white' }
  ];

  const statusIndicators = [
    { status: 'online', label: 'Dr. Smith - Available', icon: CheckCircle },
    { status: 'busy', label: 'Dr. Johnson - In Session', icon: AlertCircle },
    { status: 'away', label: 'Dr. Williams - Away', icon: Clock },
    { status: 'offline', label: 'Dr. Brown - Offline', icon: X }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-gray-50 to-clinic-blue-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="bg-clinic-blue-100 text-clinic-blue-800 text-lg px-6 py-3 rounded-full mb-8">
            <Award className="w-5 h-5 mr-2" />
            Advanced Design System
          </Badge>
          
          <h1 className="text-6xl text-gray-900 mb-6 leading-tight">
            Medical UI Components
            <span className="text-clinic-blue-600 block">& Design Patterns</span>
          </h1>
          
          <p className="text-2xl text-gray-600 max-w-4xl mx-auto mb-12">
            Comprehensive design system showcasing modern iconography, enhanced buttons, 
            and medical-specific UI elements for psychiatric healthcare.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { id: 'buttons', label: 'Buttons & Actions', icon: Plus },
              { id: 'icons', label: 'Medical Icons', icon: Heart },
              { id: 'cards', label: 'Cards & Layouts', icon: Grid },
              { id: 'forms', label: 'Forms & Inputs', icon: Settings },
              { id: 'status', label: 'Status & Progress', icon: Activity },
              { id: 'effects', label: 'Visual Effects', icon: Zap }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-clinic-blue-600 text-white shadow-lg'
                    : 'bg-white text-clinic-gray-700 hover:bg-clinic-gray-100 shadow-md hover:shadow-lg'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        {activeTab === 'buttons' && (
          <div className="space-y-12">
            <Card className="card-elevated">
              <CardHeader>
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <Plus className="w-6 h-6 mr-3 text-clinic-blue-600" />
                  Enhanced Button System
                </h3>
                <p className="text-gray-600">Modern button variants with medical-specific styling and improved accessibility.</p>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Button Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {buttonVariants.map((btn, index) => (
                    <div key={index} className="space-y-3">
                      <Button className={`w-full ${btn.className}`}>
                        <Heart className="w-5 h-5 mr-2" />
                        {btn.label}
                      </Button>
                      <p className="text-sm text-gray-500 text-center">{btn.variant} • {btn.size}</p>
                    </div>
                  ))}
                </div>

                {/* Button Groups */}
                <div className="space-y-4">
                  <h4 className="text-lg font-medium">Button Groups & Special Actions</h4>
                  <div className="flex flex-wrap gap-4">
                    <div className="btn-group">
                      <Button className="bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                        <Video className="w-4 h-4 mr-2" />
                        Start Call
                      </Button>
                      <Button className="bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule
                      </Button>
                      <Button className="bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Message
                      </Button>
                    </div>
                  </div>

                  {/* Loading States */}
                  <div className="flex gap-4">
                    <Button 
                      className={`bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white ${loading ? 'btn-loading' : ''}`}
                      onClick={() => {
                        setLoading(true);
                        setTimeout(() => setLoading(false), 2000);
                      }}
                    >
                      {!loading && <Play className="w-4 h-4 mr-2" />}
                      {loading ? 'Processing...' : 'Start Treatment'}
                    </Button>
                    
                    <Button className="btn-floating">
                      <Plus className="w-6 h-6" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'icons' && (
          <div className="space-y-12">
            <Card className="card-elevated">
              <CardHeader>
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <Heart className="w-6 h-6 mr-3 text-clinic-blue-600" />
                  Medical Icon System
                </h3>
                <p className="text-gray-600">Professional medical icons with themed containers and enhanced visual hierarchy.</p>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Icon Sizes */}
                <div>
                  <h4 className="text-lg font-medium mb-4">Icon Size Variations</h4>
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <Heart className="icon-sm text-clinic-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">Small (16px)</p>
                    </div>
                    <div className="text-center">
                      <Heart className="icon-md text-clinic-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">Medium (20px)</p>
                    </div>
                    <div className="text-center">
                      <Heart className="icon-lg text-clinic-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">Large (24px)</p>
                    </div>
                    <div className="text-center">
                      <Heart className="icon-xl text-clinic-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">XL (32px)</p>
                    </div>
                    <div className="text-center">
                      <Heart className="icon-2xl text-clinic-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">2XL (40px)</p>
                    </div>
                    <div className="text-center">
                      <Heart className="icon-3xl text-clinic-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">3XL (48px)</p>
                    </div>
                  </div>
                </div>

                {/* Medical Icons Grid */}
                <div>
                  <h4 className="text-lg font-medium mb-6">Medical Icon Collection</h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {medicalIcons.map((item, index) => (
                      <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow duration-200">
                        <div className={`icon-container icon-container-xl icon-${item.theme} mx-auto mb-4 hover-scale`}>
                          <item.icon className="icon-xl" />
                        </div>
                        <h5 className="font-medium text-gray-900 mb-2">{item.name}</h5>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Icon Containers */}
                <div>
                  <h4 className="text-lg font-medium mb-4">Enhanced Icon Containers</h4>
                  <div className="flex flex-wrap gap-6">
                    <div className="icon-container icon-container-lg icon-medical hover-glow">
                      <Stethoscope className="icon-lg" />
                    </div>
                    <div className="icon-container icon-container-lg icon-therapy animate-pulse-slow">
                      <Brain className="icon-lg" />
                    </div>
                    <div className="icon-container icon-container-lg icon-emergency hover-lift">
                      <AlertCircle className="icon-lg" />
                    </div>
                    <div className="icon-container icon-container-lg icon-success animate-float">
                      <CheckCircle className="icon-lg" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'cards' && (
          <div className="space-y-12">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Standard Card */}
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="icon-container icon-container-md icon-medical">
                      <Heart className="icon-md" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Standard Card</h4>
                      <p className="text-sm text-gray-600">Basic card with elevation</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">Enhanced card styling with consistent spacing and medical theming.</p>
                  <Button className="w-full bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                    Learn More
                  </Button>
                </CardContent>
              </Card>

              {/* Interactive Card */}
              <Card className="card-interactive">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="icon-container icon-container-md icon-therapy">
                      <Brain className="icon-md" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Interactive Card</h4>
                      <p className="text-sm text-gray-600">Hover for animations</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">Cards with hover effects and interactive animations for better UX.</p>
                  <Button className="w-full btn-ghost">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Explore Features
                  </Button>
                </CardContent>
              </Card>

              {/* Glass Card */}
              <Card className="card-glass">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="icon-container icon-container-md icon-info">
                      <Shield className="icon-md" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Glassmorphism</h4>
                      <p className="text-sm text-gray-600">Modern glass effect</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">Advanced glassmorphism effects with backdrop blur and transparency.</p>
                  <Button className="w-full bg-white/20 backdrop-blur-sm border border-white/30 text-gray-800 hover:bg-white/30">
                    Secure Access
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Medical Specific Cards */}
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="card-medical">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="icon-container icon-container-lg icon-medical">
                      <Activity className="icon-lg" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-clinic-blue-800 mb-2">Patient Vitals</h4>
                      <p className="text-gray-600 mb-4">Real-time monitoring and health tracking dashboard.</p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Heart Rate</span>
                          <span className="text-sm font-medium">72 BPM</span>
                        </div>
                        <div className="progress-bar_web_web">
                          <div className="progress-bar_web_web-fill" style={{ width: '72%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-emergency">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="icon-container icon-container-lg icon-emergency animate-pulse">
                      <AlertCircle className="icon-lg" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-800 mb-2">Emergency Alert</h4>
                      <p className="text-gray-600 mb-4">Critical patient status requiring immediate attention.</p>
                      <Button className="w-full btn-emergency">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Emergency Team
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'forms' && (
          <div className="space-y-12">
            <Card className="card-elevated max-w-2xl mx-auto">
              <CardHeader>
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <Settings className="w-6 h-6 mr-3 text-clinic-blue-600" />
                  Enhanced Form Elements
                </h3>
                <p className="text-gray-600">Modern form styling with floating labels and medical theming.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Floating Label Inputs */}
                <div className="space-y-4">
                  <div className="input-floating">
                    <Input 
                      type="text" 
                      placeholder="Patient Name"
                      className="input-medical"
                    />
                    <label>Patient Full Name</label>
                  </div>
                  
                  <div className="input-floating">
                    <Input 
                      type="email" 
                      placeholder="Email Address"
                      className="input-medical"
                    />
                    <label>Email Address</label>
                  </div>
                  
                  <div className="input-floating">
                    <Input 
                      type="tel" 
                      placeholder="Phone Number"
                      className="input-medical"
                    />
                    <label>Phone Number</label>
                  </div>
                </div>

                {/* Input with Icons */}
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input 
                      type="text" 
                      placeholder="Search patients..."
                      className="input-medical pl-10"
                    />
                  </div>
                  
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input 
                      type="email" 
                      placeholder="healthcare@example.com"
                      className="input-medical pl-10"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-4">
                  <Button className="flex-1 bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Patient Info
                  </Button>
                  <Button className="btn-ghost">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'status' && (
          <div className="space-y-12">
            <Card className="card-elevated">
              <CardHeader>
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <Activity className="w-6 h-6 mr-3 text-clinic-blue-600" />
                  Status Indicators & Progress
                </h3>
                <p className="text-gray-600">Real-time status updates and progress tracking for medical workflows.</p>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Provider Status */}
                <div>
                  <h4 className="text-lg font-medium mb-4">Provider Availability</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {statusIndicators.map((item, index) => (
                      <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                        <item.icon className={`w-5 h-5 ${
                          item.status === 'online' ? 'text-green-600' :
                          item.status === 'busy' ? 'text-red-600' :
                          item.status === 'away' ? 'text-amber-600' :
                          'text-gray-600'
                        }`} />
                        <span className={`status-indicator status-${item.status}`}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                        <span className="text-gray-700">{item.label.split(' - ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Progress Bars */}
                <div>
                  <h4 className="text-lg font-medium mb-4">Treatment Progress</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Therapy Sessions</span>
                        <span className="text-sm text-gray-600">{progress}%</span>
                      </div>
                      <div className="progress-bar_web_web progress-bar_web_web-lg">
                        <div 
                          className="progress-bar_web_web-fill bg-clinic-blue-600" 
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Medication Compliance</span>
                        <span className="text-sm text-gray-600">90%</span>
                      </div>
                      <div className="progress-bar_web_web progress-bar_web_web-lg">
                        <div 
                          className="progress-bar_web_web-fill bg-green-600" 
                          style={{ width: '90%' }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Recovery Goals</span>
                        <span className="text-sm text-gray-600">60%</span>
                      </div>
                      <div className="progress-bar_web_web progress-bar_web_web-lg">
                        <div 
                          className="progress-bar_web-fill bg-amber-500" 
                          style={{ width: '60%' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 mt-4">
                    <Button 
                      onClick={() => setProgress(Math.min(100, progress + 10))}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Update Progress
                    </Button>
                    <Button 
                      onClick={() => setProgress(Math.max(0, progress - 10))}
                      className="btn-ghost"
                    >
                      Reset
                    </Button>
                  </div>
                </div>

                {/* Medical Badges */}
                <div>
                  <h4 className="text-lg font-medium mb-4">Medical Status Badges</h4>
                  <div className="flex flex-wrap gap-3">
                    <span className="badge-medical">
                      <Shield className="w-4 h-4 mr-1" />
                      HIPAA Compliant
                    </span>
                    <span className="badge-emergency">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Critical
                    </span>
                    <span className="badge-success">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Treatment Complete
                    </span>
                    <span className="badge-warning">
                      <Clock className="w-4 h-4 mr-1" />
                      Follow-up Required
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'effects' && (
          <div className="space-y-12">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Hover Effects */}
              <Card className="card-elevated">
                <CardHeader>
                  <h4 className="font-semibold flex items-center">
                    <Zap className="w-5 h-5 mr-2 text-clinic-blue-600" />
                    Hover Effects
                  </h4>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="hover-lift p-4 bg-clinic-blue-50 rounded-lg cursor-pointer text-center">
                    <Heart className="w-8 h-8 text-clinic-blue-600 mx-auto mb-2" />
                    <p>Lift on Hover</p>
                  </div>
                  <div className="hover-scale p-4 bg-clinic-gold-50 rounded-lg cursor-pointer text-center">
                    <Star className="w-8 h-8 text-clinic-gold-600 mx-auto mb-2" />
                    <p>Scale on Hover</p>
                  </div>
                  <div className="hover-glow p-4 bg-clinic-gray-50 rounded-lg cursor-pointer text-center">
                    <Zap className="w-8 h-8 text-clinic-blue-600 mx-auto mb-2" />
                    <p>Glow Effect</p>
                  </div>
                </CardContent>
              </Card>

              {/* Animations */}
              <Card className="card-elevated">
                <CardHeader>
                  <h4 className="font-semibold flex items-center">
                    <Play className="w-5 h-5 mr-2 text-clinic-blue-600" />
                    Animations
                  </h4>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="animate-fade-in p-4 bg-clinic-blue-50 rounded-lg text-center">
                    <Activity className="w-8 h-8 text-clinic-blue-600 mx-auto mb-2" />
                    <p>Fade In</p>
                  </div>
                  <div className="animate-float p-4 bg-clinic-gold-50 rounded-lg text-center">
                    <Heart className="w-8 h-8 text-clinic-gold-600 mx-auto mb-2" />
                    <p>Floating</p>
                  </div>
                  <div className="animate-pulse-slow p-4 bg-green-50 rounded-lg text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p>Pulse Slow</p>
                  </div>
                </CardContent>
              </Card>

              {/* Gradients */}
              <Card className="card-elevated">
                <CardHeader>
                  <h4 className="font-semibold flex items-center">
                    <PieChart className="w-5 h-5 mr-2 text-clinic-blue-600" />
                    Gradients
                  </h4>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-clinic-gradient p-4 rounded-lg text-center text-white">
                    <Shield className="w-8 h-8 mx-auto mb-2" />
                    <p>Clinical Blue</p>
                  </div>
                  <div className="bg-clinic-gradient-gold p-4 rounded-lg text-center text-white">
                    <Award className="w-8 h-8 mx-auto mb-2" />
                    <p>Gold Accent</p>
                  </div>
                  <div className="bg-gradient-medical p-4 rounded-lg text-center text-white">
                    <Stethoscope className="w-8 h-8 mx-auto mb-2" />
                    <p>Medical Theme</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};